import { filterByBPA, filterByDepartment, filterByDivision, filterByQuartile, filterByQuartileDiffs, filterByResultType, filterByServiceType } from '../DepartmentScoring/departmentScoringHelpers.js';
import { utils, read } from 'xlsx';
import { SCORE_TYPE_NAME_FINAL_ID, TABLE_COLUMN_WIDTH, USER_ROLE } from '../../static/constants.js';
import { downloadBulkUploadScoresSpreadsheet } from '../../services/downloadService.js';
import ErrorIconColumn from '../reusable/AgGridCells/ErrorIconCell/ErrorIconCell.js';
import { constructMissingColumnsMessage, normalizeColumnName, normalizeRowsToExpectedColumns, trimValue } from '../reusable/reusableFunctions.js';
import { messageManager } from '../System/messageManager/messageManager.js';
import { FILTER_TYPES } from '../reusable/Filters/filterHelpers.js';
import { ERROR, PRIORITY, PROGRAM, USER_GROUP } from '../../static/keyWords.js';
import { EDIT_TYPE_LABELS } from '../../static/constants.js';
import { filterByActiveCostCenters, filterByUserAssignedCostCenters } from '../../services/programService.js';

export const ACCEPTED_FILES = ['.xlsx', '.xls'];
export const INITIAL_FILE_STATE = { name: '', description: '', file: '' };

export const constantColumns = (nameConventions) => {

  return {
    ACTION_EDIT: 'Action(Edit)',
    SERVICE_TYPE: 'ServiceType',
    USER_GROUPS: `${nameConventions[USER_GROUP].pluralFormAlias}`,
    PROGRAM_NUM: 'Program number',
    PROGRAM: `${nameConventions[PROGRAM].alias}`,
    PROGRAM_DESCRIPTION: `${nameConventions[PROGRAM].alias} description`,
    RESULT_TYPE: `${nameConventions[PRIORITY].alias} type`,
    RESULT_ABBR: `${nameConventions[PRIORITY].alias} abbr`,
    FINAL_SCORE: 'Final score',
    FINAL_SCORE_OPTION: 'Final score option',
    FINAL_COMMENTS: 'Final comments',
    DEPT_SCORE: 'Dept score',
    DEPT_SCORE_OPTION: 'Department score option',
    DEPT_COMMENTS: 'Department comments',
    RESULT_ID: 'ResultId',
    PROGRAM_ID: 'ProgId',
  };
};

export const getRenderCondition = (loadFile, uploadedFile) => {
  return loadFile && uploadedFile.name;
};

export const MORE_MENU_OPTIONS = (user) => {
  const result = [];

  if (user?.role === USER_ROLE.ADMIN) {
    result.push({
      label: 'Recommend',
      value: 'recommend',
      useAiIcon: true,
      leadingIconType: 'component',
      leadingIconComponentProps: { external: true, externalType: 'all' },
      disabled: user?.isViewOnly,
    });
  }

  result.push({
    label: 'Quartile boundaries',
    value: 'quartile',
    icon: 'chart_scatter_plot',
    leadingIconType: 'component',
    leadingIconComponentProps: { external: true, externalType: 'all' },
    disabled: user?.isViewOnly,
  });

  if (user?.role === USER_ROLE.ADMIN) {
    result.push({
      label: 'Apply scores',
      value: 'applyScores',
      icon: 'sparkles',
      leadingIconType: 'component',
      leadingIconComponentProps: { external: true, externalType: 'all' },
    });
  }

  return result;
};

export const fireSelectedDialog = (e, handleRecommendButton, setOpenQuartileDialog, handleApplyScoresButton) => {
  switch (e.detail.value) {
    case 'recommend':
      handleRecommendButton();
      break;
    case 'quartile':
      setOpenQuartileDialog(true);
      break;
    case 'applyScores':
      handleApplyScoresButton();
      break;
    default:
      break;
  }
};

const defaultTemplate = (nameConventions) => {
  return {
    action: constantColumns(nameConventions).ACTION_EDIT,
    serviceType: constantColumns(nameConventions).SERVICE_TYPE,
    costCenter: constantColumns(nameConventions).USER_GROUPS,
    programNum: constantColumns(nameConventions).PROGRAM_NUM,
    program: constantColumns(nameConventions).PROGRAM,
    programDescription: constantColumns(nameConventions).PROGRAM_DESCRIPTION,
    resultType: constantColumns(nameConventions).RESULT_TYPE,
    resultAbbr: constantColumns(nameConventions).RESULT_ABBR,
    finalScore: constantColumns(nameConventions).FINAL_SCORE,
    finalScoreOption: constantColumns(nameConventions).FINAL_SCORE_OPTION,
    finalComments: constantColumns(nameConventions).FINAL_COMMENTS,
    deptScore: constantColumns(nameConventions).DEPT_SCORE,
    departmentScoreOptionName: constantColumns(nameConventions).DEPT_SCORE_OPTION,
    departmentComments: constantColumns(nameConventions).DEPT_COMMENTS,
    result_id: constantColumns(nameConventions).RESULT_ID,
    program_id: constantColumns(nameConventions).PROGRAM_ID,
  };
};

export const mapDataToSendToBackend = (rows, nameConventions) => {
  const template = defaultTemplate(nameConventions);
  let data = [];
  for (let row of rows) {
    let obj = {};
    for (let key in template) {
      obj[key] = key === 'action'
        ? trimValue(row[template[key]])
        : row[template[key]];
      obj[key] = key === 'result_id' ? Number(row[template[key]]) : row[template[key]];
    }
    data.push(obj);
  }
  return data;
};

export const getDefaultColumns = (showColumns, nameConventions) => {
  return [
    {
      headerName: 'Quartile',
      field: 'finalQuartileVerbal',
      editable: false,
      context: 'Quartile',
      hide: !showColumns?.find((column) => column.name === 'Quartile')?.show,
      width: TABLE_COLUMN_WIDTH.MD,
    },
    {
      headerName: 'Service type',
      field: 'serviceType.name',
      editable: false,
      context: 'Service Type',
      hide: !showColumns?.find((column) => column.name === 'Service Type')?.show,
      width: TABLE_COLUMN_WIDTH.MD,
    },
    {
      headerName: nameConventions[PROGRAM].alias || PROGRAM,
      field: 'name',
      editable: false,
      context: PROGRAM,
      hide: !showColumns?.find((column) => column.name === 'Program')?.show,
      pinned: 'left',
      lockPosition: 'left',
      resizable: false,
      width: TABLE_COLUMN_WIDTH.XXL,
    },
    // uncomment if the release from team A contains Program Description inside the show columns
    // { 
    //   headerName: 'Program description', 
    //   field: 'description', 
    //   editable: false,
    //   context: 'Program Description',
    //   hide: !showColumns?.find((column) => column.name === 'Program Description')?.show,
    // },
    {
      headerName: 'Peer',
      field: 'finalScore',
      editable: false,
      valueFormatter: (params) => params?.value?.toFixed(2),
      context: 'Peer',
      hide: !showColumns?.find((column) => column.name === 'Peer')?.show,
      width: TABLE_COLUMN_WIDTH.XS,
    },
    {
      headerName: 'Department',
      field: 'deptScore',
      editable: false,
      valueFormatter: (params) => params?.value?.toFixed(2),
      context: 'Department',
      hide: !showColumns?.find((column) => column.name === 'Department')?.show,
      width: TABLE_COLUMN_WIDTH.XS,
    },
    {
      headerName: 'Actions',
      cellRenderer: 'agGroupCellRenderer',
      cellClass: 'grid-cell-centered-action',
      filter: false,
      sortable: false,
      suppressColumnsToolPanel: true,
      pinned: 'right',
      resizable: false,
      editable: false,
      cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      width: TABLE_COLUMN_WIDTH.XS,
    },
  ];
};

export const parseFile = async (file, setColumns, setRows, setError, retrieveColumnsFromStorage, setTotalCount, columnsOrderAndSize, nameConventions) => {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = read(arrayBuffer, { type: 'array' });
  let _columns = [];
  let _rows = [];

  if (Object.keys(workbook.Sheets).every(sh => sh !== 'Scores')) {
    setError([messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.MISSING_SCORES_SHEET]);
    return;
  }

  const worksheet = workbook.Sheets[workbook.SheetNames.filter(sh=>sh === 'Scores')[0]];
  const csv_arr = utils.sheet_to_json(worksheet, {
    defval: '',
  });

  if (csv_arr.length === 0) {
    setError([messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.EMPTY_FILE]);
    return;
  }

  const { rows: normalizedRows, columns: excel_cols } = normalizeRowsToExpectedColumns(
    csv_arr,
    getDefaultExpectedColumns(nameConventions)
  );

  const error = checkColumns(excel_cols, nameConventions);

  if (error.length > 0) {
    setError(constructMissingColumnsMessage(error));
    return;
  }

  const actionColumn = constantColumns(nameConventions).ACTION_EDIT;
  normalizedRows.forEach((row) => {
    row[actionColumn] = trimValue(row[actionColumn]);
  });

  _columns = generateUploadColumnDefs(excel_cols, nameConventions);
  _columns.push(ErrorIconColumn());
  _rows.push(normalizedRows);

  columnsOrderAndSize?.['finalReview-scoresUploadValidationColumns']?.length > 0
    ? setColumns(retrieveColumnsFromStorage(_columns, 'finalReview-scoresUploadValidationColumns'))
    : setColumns(_columns);
  setRows(..._rows);
  setTotalCount(_rows[0].length);
};

const generateUploadColumnDefs = (columns, nameConventions) => {
  return columns.map((col, ind) => ({
    colId: `${ind}`,
    context: col,
    headerName: col,
    headerTooltip: col,
    tooltipField: col,
    field: col,
    filter: false,
    suppressFloatingFilterButton: true,
    floatingFilter: false,
    pinned: col === constantColumns(nameConventions).ACTION_EDIT ? 'left' : false,
    lockPosition: col === constantColumns(nameConventions).ACTION_EDIT ? 'left' : false,
    resizable: col === constantColumns(nameConventions).ACTION_EDIT ? false : true,
    cellEditorPopup: true,
    //cellRenderer: (params) => <>{params.data[col]}</>, // don't do that blindly - investigate/check - this expects a custom component!
    cellClass: 'grid-cell-centered',
    width: col === constantColumns(nameConventions).ACTION_EDIT ? 150 : undefined,
    cellEditor: col === constantColumns(nameConventions).ACTION_EDIT ? 'agRichSelectCellEditor' : 'agLargeTextCellEditor',
    cellEditorParams: col === constantColumns(nameConventions).ACTION_EDIT ? { values: [EDIT_TYPE_LABELS.EDIT, EDIT_TYPE_LABELS.IGNORE] } : undefined,
    cellStyle: { display: 'block' },
  }));
};

// const uploadColumnDefsWidths = (col) => {
//   switch (col) {
//     case constantColumns().PROGRAM_DESCRIPTION:
//     case constantColumns().FINAL_SCORE_OPTION:
//     case constantColumns().FINAL_COMMENTS:
//     case constantColumns().DEPT_SCORE_OPTION:
//     case constantColumns().DEPT_COMMENTS:
//       return 400;
//     case ERROR:
//       return 300;
//     case constantColumns().PROGRAM:
//     case constantColumns().USER_GROUPS:
//     case constantColumns().RESULT_TYPE:
//     case constantColumns().RESULT_ABBR:
//     case constantColumns().SERVICE_TYPE:
//       return 200;
//     case constantColumns().PROGRAM_NUM:
//     case constantColumns().FINAL_SCORE:
//     case constantColumns().DEPT_SCORE:
//     case constantColumns().ACTION_EDIT:
//     case constantColumns().RESULT_ID:
//     case constantColumns().PROGRAM_ID:
//       return 150;
//     default:
//       return 100;
//   }
// };

const getDefaultExpectedColumns = (nameConventions) => {
  return [
    constantColumns(nameConventions).ACTION_EDIT,
    constantColumns(nameConventions).SERVICE_TYPE,
    constantColumns(nameConventions).USER_GROUPS,
    constantColumns(nameConventions).PROGRAM_NUM,
    constantColumns(nameConventions).PROGRAM,
    constantColumns(nameConventions).PROGRAM_DESCRIPTION,
    constantColumns(nameConventions).RESULT_TYPE,
    constantColumns(nameConventions).RESULT_ABBR,
    constantColumns(nameConventions).FINAL_SCORE,
    constantColumns(nameConventions).FINAL_SCORE_OPTION,
    constantColumns(nameConventions).FINAL_COMMENTS,
    constantColumns(nameConventions).DEPT_SCORE,
    constantColumns(nameConventions).DEPT_SCORE_OPTION,
    constantColumns(nameConventions).DEPT_COMMENTS,
    constantColumns(nameConventions).RESULT_ID,
    constantColumns(nameConventions).PROGRAM_ID,
  ];
};

const checkColumns = (columns, nameConventions) => {
  let error = [];
  const normalizedColumns = columns.map((column) => normalizeColumnName(column));
  getDefaultExpectedColumns(nameConventions).forEach((col) => {
    if (!normalizedColumns.includes(normalizeColumnName(col))) {
      error.push(col);
    }
  });
  return error;
};

export const validateInput = (params, possibleResultTypes, possibleResultAbbrs, user, nameConventions) => {
  let data = params.data;
  const editType = trimValue(data[constantColumns(nameConventions).ACTION_EDIT]);

  if (!editType) {
    return [];
  }

  let errors = [];
  const resultType = user.resultTypes.find(resultType => resultType.name == data[constantColumns(nameConventions).RESULT_TYPE]);
  const scoreOptions = resultType && resultType.results.find(result => result.abbreviation == data[constantColumns(nameConventions).RESULT_ABBR])
    ? resultType.results.find(result => result.abbreviation == data[constantColumns(nameConventions).RESULT_ABBR]).scoreOptions
    : null;
  const existingResultId = user.resultTypes.map(rt=>rt.results.map(r=>r.id)).flat().includes(Number(data[constantColumns(nameConventions).RESULT_ID]));

  if (typeof editType !== 'string' || editType.toLowerCase() !== 'edit') {
    errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.ACTION_EDIT);
    return errors;
  }

  if (!existingResultId) {
    errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.WRONG_RESULT_ID);
    return errors;
  }

  if ((data[constantColumns(nameConventions).FINAL_SCORE] == null || data[constantColumns(nameConventions).FINAL_SCORE] === '') &&
    (data[constantColumns(nameConventions).DEPT_SCORE] == null || data[constantColumns(nameConventions).DEPT_SCORE] === '')) {
    errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.BOTH_SCORES_MISSING);
    return errors;
  }
  
  for (const field in data) {
    switch (field) {
      case constantColumns(nameConventions).RESULT_TYPE:
        if (!possibleResultTypes.includes(data[field])) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.RESULT_TYPE(possibleResultTypes));
          return errors;
        }
        break;
      case constantColumns(nameConventions).RESULT_ABBR:
        if (!possibleResultAbbrs[data[constantColumns(nameConventions).RESULT_TYPE]].includes(data[field])) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.RESULT_ABBR([possibleResultAbbrs, data]));
          return errors;
        }
        break;
      case constantColumns(nameConventions).FINAL_SCORE:
        if (data[field] === null || data[field] === '' || isNaN(Number(data[field]))) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.SCORE_NUMBER('final score'));
          return errors;
        } else if (data[field] !== null && !scoreOptions.find(scoreOption => scoreOption.weight == data[field])) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.SCORE_ONE_OF_FOLLOWING(scoreOptions));
          return errors;
        }
        break;
      case constantColumns(nameConventions).DEPT_SCORE:
        if (data[field] === null || data[field] === '' || isNaN(Number(data[field]))) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.SCORE_NUMBER('dept score'));
          return errors;
        } else if (data[field] !== null && !scoreOptions.find(scoreOption => scoreOption.weight == data[field])) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.SCORE_ONE_OF_FOLLOWING(scoreOptions));
          return errors;
        }
        break;
      case constantColumns(nameConventions).RESULT_ID:
        if (!Number.isInteger(Number(data[field])) || !Number(data[field])) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.ID_REQUIRED('result_id'));
          return errors;
        }
        break;
      case constantColumns(nameConventions).PROGRAM_ID:
        if (!Number.isInteger(Number(data[field])) || !Number(data[field])) {
          errors.push(messageManager(nameConventions).FINAL_REVIEW.VALIDATION_ERRORS.ID_REQUIRED('program_id'));
          return errors;
        }
        break;
      default:
        break;
    }
  }

  return errors;
};

export const filterByTagFilters = (filteredProgramsByCostCenter, appliedFilters) => {
  if (appliedFilters && appliedFilters.length > 0) {
    let filteredPrograms = [...filteredProgramsByCostCenter];
    appliedFilters.forEach((filter) => {
      switch (filter.type) {
        case FILTER_TYPES.DEPARTMENT:
          filteredPrograms = filterByDepartment(filteredPrograms, filter);
          break;
        case FILTER_TYPES.DIVISION:
          filteredPrograms = filterByDivision(filteredPrograms, filter);
          break;
        case FILTER_TYPES.SERVICE_TYPE:
          filteredPrograms = filterByServiceType(filteredPrograms, filter);
          break;
        case FILTER_TYPES.BPA:
          filteredPrograms = filterByBPA(filteredPrograms, filter, true);
          break;
        case FILTER_TYPES.RESULT:
          filteredPrograms = filterByResultType(
            filteredPrograms,
            filter,
            FILTER_TYPES.RESULT,
            true
          );
          break;
        case FILTER_TYPES.QUARTILE:
          filteredPrograms = filterByQuartile(filteredPrograms, filter);
          break;
        case FILTER_TYPES.QUARTILE_DIFF:
          filteredPrograms = filterByQuartileDiffs(
            filteredPrograms,
            filter
          );
      }
    });
    return filteredPrograms || [];
  } else {
    return filteredProgramsByCostCenter;
  }
};

export const mapFinalScoresForSaving = (newScores, editedResults) => {
  return Object.keys(newScores).reduce((scoreObjects, resultId) => {
    const result = editedResults.find(item => item.id === Number(resultId));
    const score = result?.scores.find(item => item.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);

    if (score) {
      scoreObjects.push({ ...score, comments: trimValue(score.comments) });
    }

    return scoreObjects;
  }, []);
};

export const mapQuartileBoundariesForSaving = (serviceTypes, boundaryData, selectedType) => {
  const processedData = serviceTypes.filter(type => type.name === selectedType.name).map(type => {
    return {
      id: type.id,
      name: type.name,
      scored: type.scored,
      q3q4: boundaryData[type.name][0],
      q2q3: boundaryData[type.name][1],
      q1q2: boundaryData[type.name][2],
      programs: []
    };
  });
  return processedData;
};

export const validateQuartileBoundaries = ([first, second, third], setIsDisabled) => {
  if (typeof first !== 'number' || typeof second !== 'number' || typeof third !== 'number') {
    setIsDisabled(true);
    return;
  }

  const isValid =
    first > 0 &&
    second > first &&
    third > second &&
    third <= 100;

  setIsDisabled(!isValid);
};

export const updateRowDataInGridApi = (updatedScores, gridApi) => {
  if (!updatedScores?.length || !gridApi) return;

  const rowNode = gridApi.getRowNode(updatedScores[0].programId.toString());
  if (!rowNode) return;

  rowNode.setDataValue('finalScore', updatedScores[0].finalScore);
  rowNode.setDataValue('deptScore', updatedScores[0].departmentScore);
};

export const getHeaderButtons = (loadedFile, handleNewUpload, setOpenQuartileDialog, setOpenUploadDialog, handleRecommendButton, handleApplyScoresButton, user, isExportButtonDisabled, nameConventions) => {

  if (loadedFile) return [
    {
      id: 'upload',
      name: 'New upload',
      variant: 'text',
      icon: 'upload',
      onClickFunction: () => handleNewUpload(),
    },
  ];

  return [
    {
      id: 'more',
      name: 'More',
      variant: 'text',
      icon: 'more_vert',
      type: 'dropDown',
      popOverIcon: false,
      children: MORE_MENU_OPTIONS(user),
      onClickFunction: (e) => {
        fireSelectedDialog(e, handleRecommendButton, setOpenQuartileDialog, handleApplyScoresButton);
      },
    },
    {
      id: 'export',
      name: 'Export',
      variant: 'text',
      icon: 'export',
      onClickFunction: () => downloadBulkUploadScoresSpreadsheet(user),
      disabled: isExportButtonDisabled,
      tooltip: messageManager(nameConventions).FILE_DOWNLOAD_IN_PROGRESS_MESSAGE,
    },
    {
      id: 'upload',
      name: 'Upload',
      variant: 'raised',
      before: true,
      onClickFunction: () => setOpenUploadDialog(true),
      disabled: user?.isViewOnly,
    },
  ];
};

export const renderCardTitle = (finalReviewTexts, loadFile, uploadedFile) => {
  const cardTitles = finalReviewTexts.FINAL_REVIEW;
  const isAnyFileUploaded = getRenderCondition(loadFile, uploadedFile);

  if (isAnyFileUploaded) return cardTitles.VALIDATION_TITLE;
  return cardTitles.TITLE;
};

export const handleFilterPrograms = (programList, user, appliedFilters) => {
  const activePrograms = filterByActiveCostCenters(programList, user);
  const assignedPrograms = filterByUserAssignedCostCenters(activePrograms, user);

  return filterByTagFilters(assignedPrograms, appliedFilters);
};

export const handleDefinePrograms = (showColumns, nameConventions, columnsOrderAndSize, retrieveColumnsFromStorage) => {
  const defaultColumns = getDefaultColumns(showColumns.finalScoreColumns, nameConventions);

  if (columnsOrderAndSize?.finalReviewColumns?.length) return retrieveColumnsFromStorage(defaultColumns, 'finalReviewColumns');
  return defaultColumns;

};

export const validateRows = (rows, nameConventions) => {
  if (!rows || !rows.length) return { errorCount: 0, hasUserChanges: false, hasErrors: false };

  const actionEdit = constantColumns(nameConventions).ACTION_EDIT;
  const editedRows = rows.filter((row) => row[actionEdit]);

  if (editedRows.length === 0) {
    return { errorCount: 0, hasUserChanges: false, hasErrors: false };
  }

  const errs = rows.filter((row) => row[ERROR]);

  return {
    errorCount: errs.length,
    hasUserChanges: editedRows.length > 0,
    hasErrors: editedRows.some((row) => row[ERROR]),
  };
};

export const filterResults = (resultType) => {
  return resultType.results.filter(result => {
    const finalScore = result.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID);
    return Boolean(finalScore);
  });
};

export const validateData = (_editedResults) => {
  return !_editedResults.every(result => result.scores.find(score => score.scoreTypeId === SCORE_TYPE_NAME_FINAL_ID)?.scoreOptionId);
};
